import { useMemo, useState } from 'react';
import { LuCircleHelp, LuX } from 'react-icons/lu';

import { BillingExclusion, GUID } from '@ndvr/billing-api';
import { Account } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { HintLabel } from '@/components/HintLabel';
import { Spinner } from '@/components/Spinner';
import { TickerSelectorInput } from '@/components/TickerSelectorInput';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAssetsByScIdsMap } from '@/hooks/market-data-queries';

import {
    useBillingExclusion,
    useCashEquivalentShareClasses,
    useSetBillingExclusion,
} from './billing-exclusion-queries';

// Popover listing the fixed set of funds the "exclude cash equivalents" toggle covers.
const CashEquivalentsHelp = ({
    shareClassIds,
    assetByScId,
}: {
    shareClassIds: string[];
    assetByScId: Map<string, AssetInfo>;
}) => (
    <Popover>
        <PopoverTrigger asChild>
            <button
                type="button"
                aria-label="Which funds count as cash equivalents?"
                className="text-muted-foreground hover:text-foreground"
            >
                <LuCircleHelp className="h-3.5 w-3.5" />
            </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96">
            <p className="mb-2 text-xs font-medium">Cash equivalent funds</p>
            <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto text-xs">
                {shareClassIds
                    .map((id) => ({ id, asset: assetByScId.get(id) }))
                    .sort((a, b) =>
                        (a.asset?.symbol ?? a.id).localeCompare(b.asset?.symbol ?? b.id, undefined, {
                            sensitivity: 'base',
                        }),
                    )
                    .map(({ id, asset }) => (
                        <li key={id} className="flex justify-between gap-2">
                            <span className="font-medium">{asset?.symbol ?? id}</span>
                            {asset?.description && (
                                <span className="text-muted-foreground truncate">{asset.description}</span>
                            )}
                        </li>
                    ))}
            </ul>
        </PopoverContent>
    </Popover>
);

interface ExclusionEditorProps {
    accountGUID: GUID<Account>;
    initialExclusion: BillingExclusion;
    onClose: () => void;
}

const ExclusionEditor = ({ accountGUID, initialExclusion, onClose }: ExclusionEditorProps) => {
    const setMutation = useSetBillingExclusion();
    const { data: cashEquivalentShareClassIds = [] } = useCashEquivalentShareClasses();

    const [excludedCash, setExcludedCash] = useState(initialExclusion.excludedCash);
    const [excluded, setExcluded] = useState<string[]>(initialExclusion.excludedShareClassIds);

    const scIds = useMemo(() => [...excluded, ...cashEquivalentShareClassIds], [excluded, cashEquivalentShareClassIds]);
    const { data: assetByScId } = useAssetsByScIdsMap(scIds);

    const addShareClass = (asset: AssetInfo) => {
        if (!asset.scId || excluded.includes(asset.scId)) return;
        setExcluded((prev) => [...prev, asset.scId!]);
    };
    const removeShareClass = (scId: string) => setExcluded((prev) => prev.filter((id) => id !== scId));

    const onSave = async () => {
        await setMutation.mutateAsync({
            accountGUID,
            request: { excludedShareClassIds: excluded, excludedCash },
        });
        onClose();
    };

    return (
        <div className="flex min-h-0 flex-col gap-4">
            <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={excludedCash} onCheckedChange={(checked) => setExcludedCash(checked === true)} />
                <span>Exclude cash equivalents</span>
                {cashEquivalentShareClassIds.length > 0 && (
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        ({cashEquivalentShareClassIds.length} share classes)
                        <CashEquivalentsHelp shareClassIds={cashEquivalentShareClassIds} assetByScId={assetByScId} />
                    </span>
                )}
            </label>

            <div className="flex flex-col gap-2">
                <span className="text-muted-foreground text-xs font-medium">Excluded share classes</span>
                <TickerSelectorInput
                    // Remount on each add to clear the field (and re-focus via autoFocus).
                    key={excluded.length}
                    label=""
                    placeholder="Exclude a share class by ticker…"
                    autoFocus
                    usePortal
                    // Billing exclusions name a share class the client already holds, rather than
                    // choosing something to hold, so otherwise unselectable securities stay available.
                    allowBlockedSecurities
                    excludeItems={excluded.map((scId) => ({
                        // sId needed for the selector's sId+scId match to hide excluded classes.
                        sId: assetByScId.get(scId)?.sId ?? '',
                        scId,
                    }))}
                    onSelectTicker={addShareClass}
                />
                {excluded.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {excluded.map((scId) => {
                            const asset = assetByScId.get(scId);
                            return (
                                <Badge key={scId} variant="secondary" className="gap-1">
                                    <HintLabel hint={asset?.description}>{asset?.symbol ?? scId}</HintLabel>
                                    <button
                                        type="button"
                                        aria-label={`Remove ${asset?.symbol ?? scId}`}
                                        onClick={() => removeShareClass(scId)}
                                        className="hover:text-destructive"
                                    >
                                        <LuX className="h-3 w-3" />
                                    </button>
                                </Badge>
                            );
                        })}
                    </div>
                )}
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={setMutation.isPending}>
                    Cancel
                </Button>
                <Button type="button" onClick={onSave} disabled={setMutation.isPending}>
                    {setMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
            </DialogFooter>
        </div>
    );
};

interface EditExclusionsDialogProps {
    accountGUID: GUID<Account>;
    accountName: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const EditExclusionsDialog = ({ accountGUID, accountName, open, onOpenChange }: EditExclusionsDialogProps) => {
    // Load on open so the editor seeds from the account's freshly read exclusion.
    const { data: exclusion, isLoading } = useBillingExclusion(open ? accountGUID : undefined);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[560px]" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Edit Exclusions — {accountName}</DialogTitle>
                </DialogHeader>

                {isLoading || !exclusion ? (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ) : (
                    <ExclusionEditor
                        accountGUID={accountGUID}
                        initialExclusion={exclusion}
                        onClose={() => onOpenChange(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
